/**
 * Primary jersey colors for the 48 WC 2026 nations.
 * Used by the Matchday card's "split" theme to two-tone the background.
 *
 * Keyed by ISO2 (lowercase). Missing nations fall back to brand green/gold.
 * Pairs are { primary, accent } — primary is the dominant jersey color,
 * accent is the secondary used for trim/contrast.
 *
 * Where the brand-default colors look visually similar between two countries
 * (e.g. Spain vs Portugal — both red), the admin can override per-card.
 */
export interface NationColorPair {
  primary: string;
  accent: string;
}

export const NATION_COLORS: Record<string, NationColorPair> = {
  // ── Hosts ─────────────────────────────────────────────
  us: { primary: "#FFFFFF", accent: "#B31942" },
  ca: { primary: "#FF0000", accent: "#FFFFFF" },
  mx: { primary: "#006847", accent: "#FFFFFF" },

  // ── UEFA ─────────────────────────────────────────────
  gb: { primary: "#FFFFFF", accent: "#CE1124" }, // England (legacy GB code)
  en: { primary: "#FFFFFF", accent: "#CE1124" }, // England
  fr: { primary: "#002654", accent: "#FFFFFF" },
  de: { primary: "#000000", accent: "#FFCE00" },
  es: { primary: "#AA151B", accent: "#FFC400" },
  pt: { primary: "#FF0000", accent: "#006400" },
  it: { primary: "#0066B3", accent: "#FFFFFF" },
  nl: { primary: "#FF6600", accent: "#000000" },
  be: { primary: "#E30613", accent: "#FFEC00" },
  hr: { primary: "#E03234", accent: "#FFFFFF" },
  ch: { primary: "#FF0000", accent: "#FFFFFF" }, // Switzerland
  dk: { primary: "#C8102E", accent: "#FFFFFF" },
  pl: { primary: "#FFFFFF", accent: "#DC143C" },
  rs: { primary: "#C6363C", accent: "#FFFFFF" },
  tr: { primary: "#E30A17", accent: "#FFFFFF" },
  no: { primary: "#BA0C2F", accent: "#003087" },
  se: { primary: "#FFCD00", accent: "#005AA7" },
  at: { primary: "#FFFFFF", accent: "#ED2939" }, // Austria
  cz: { primary: "#D7141A", accent: "#11457E" },
  wl: { primary: "#C8102E", accent: "#FFC700" }, // Wales (synthetic code)

  // ── CONMEBOL ─────────────────────────────────────────
  br: { primary: "#FFCC00", accent: "#009C3B" },
  ar: { primary: "#75AADB", accent: "#FFFFFF" },
  uy: { primary: "#5CACE2", accent: "#FFFFFF" },
  co: { primary: "#FCD116", accent: "#003893" },
  ec: { primary: "#FFD100", accent: "#003893" },
  pe: { primary: "#D91023", accent: "#FFFFFF" },
  cl: { primary: "#D52B1E", accent: "#0033A0" },
  py: { primary: "#D52B1E", accent: "#0033A0" },
  ve: { primary: "#75272D", accent: "#FFCD00" },
  bo: { primary: "#007934", accent: "#FFE800" },

  // ── AFC ──────────────────────────────────────────────
  jp: { primary: "#000080", accent: "#FFFFFF" },
  kr: { primary: "#CD2E3A", accent: "#0047A0" },
  ir: { primary: "#FFFFFF", accent: "#239F40" },
  sa: { primary: "#FFFFFF", accent: "#006C35" },
  au: { primary: "#FFCC00", accent: "#003D2B" },
  qa: { primary: "#8A1538", accent: "#FFFFFF" },
  iq: { primary: "#017F3D", accent: "#FFFFFF" },
  uz: { primary: "#FFFFFF", accent: "#1EB53A" },

  // ── CAF ──────────────────────────────────────────────
  ma: { primary: "#C1272D", accent: "#006233" },
  sn: { primary: "#00853F", accent: "#FDEF42" },
  ng: { primary: "#008751", accent: "#FFFFFF" },
  eg: { primary: "#CE1126", accent: "#000000" },
  tn: { primary: "#E70013", accent: "#FFFFFF" },
  dz: { primary: "#006233", accent: "#FFFFFF" },
  cm: { primary: "#007A5E", accent: "#CE1126" },
  gh: { primary: "#CE1126", accent: "#FCD116" },
  ci: { primary: "#FF7900", accent: "#009E60" },
  za: { primary: "#FFCD00", accent: "#007A4D" },

  // ── CONCACAF ─────────────────────────────────────────
  cr: { primary: "#E70013", accent: "#0033A0" },
  pa: { primary: "#DA121A", accent: "#005293" },
  hn: { primary: "#FFFFFF", accent: "#0073CF" },
  jm: { primary: "#FED100", accent: "#009B3A" },

  // ── OFC ──────────────────────────────────────────────
  nz: { primary: "#FFFFFF", accent: "#000000" },
};

/** Brand defaults when a nation isn't in the lookup. */
const FALLBACK: NationColorPair = {
  primary: "#0D8C3C",
  accent: "#FFD700",
};

export function getNationColors(iso2: string | undefined | null): NationColorPair {
  if (!iso2) return FALLBACK;
  return NATION_COLORS[iso2.toLowerCase()] ?? FALLBACK;
}
